import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdvisorKeys } from "web_myplann/Api/Advisor/AdvisorKeys";
import { assignedAgentsQueryOptions } from "web_myplann/Api/Advisor/assignedAgentsQueryOptions";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import type { ChatPageState } from "web_myplann/Chat/Hooks/useChatPage";
import {
	getAgentIdV2,
	UserAgentStatusEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { device } from "web_myplann/Utils/device";

export const useContactAdvisor = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const hasGoalsRight = useGoalsRightOptional();

	const openChat = useCallback(
		async (message?: string) => {
			const assigned = await queryClient
				.ensureQueryData(
					assignedAgentsQueryOptions(UserAgentStatusEnum.Connected),
				)
				.catch(() => undefined);

			const advisorId = assigned?.data?.[0]?.id;
			if (!advisorId) return;

			const agent = await queryClient
				.ensureQueryData({
					queryKey: AdvisorKeys.agentDetail(advisorId),
					queryFn: processResponse(() => getAgentIdV2(advisorId)),
				})
				.catch(() => undefined);

			if (hasGoalsRight) {
				const conversationId = agent?.conversationId;
				navigate(
					Routing.Chat.Detail.path(
						conversationId ? String(conversationId) : "new",
						agent?.uid,
					),
					{ state: { draftMessage: message } satisfies ChatPageState },
				);
				return;
			}

			device.navigateChatRequest(
				String(agent?.conversationId ?? ""),
				agent?.conversationTitle ?? agent?.name ?? "",
				agent?.uid,
				message,
			);
		},
		[queryClient, navigate, hasGoalsRight],
	);

	return { openChat };
};
